/**
 * REST calls for workspace (tenant) administration.
 */
export async function createWorkspace(apiBase, bearerToken, name) {
  const r = await fetch(`${apiBase}/auth/workspaces`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${bearerToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ name: (name || '').trim() }),
  });
  const text = await r.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { message: text };
  }
  if (!r.ok) {
    throw new Error(data.message || data.error || r.statusText || 'Create workspace failed');
  }
  return data;
}

export async function updateWorkspaceName(apiBase, bearerToken, tenantId, name) {
  const enc = encodeURIComponent(tenantId);
  const r = await fetch(`${apiBase}/auth/workspaces/${enc}`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${bearerToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ name: (name || '').trim() }),
  });
  const text = await r.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { message: text };
  }
  if (!r.ok) {
    throw new Error(data.message || data.error || r.statusText || 'Update failed');
  }
  return data;
}

export async function listWorkspaceMembers(apiBase, bearerToken, tenantId) {
  const enc = encodeURIComponent(tenantId);
  const r = await fetch(`${apiBase}/auth/workspaces/${enc}/members`, {
    headers: { Authorization: `Bearer ${bearerToken}` },
  });
  const text = await r.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { message: text };
  }
  if (!r.ok) {
    throw new Error(data.message || data.error || r.statusText || 'Failed to load members');
  }
  return data;
}

export async function updateWorkspaceMemberRole(apiBase, bearerToken, tenantId, userSub, { role }) {
  const enc = encodeURIComponent(tenantId);
  const subEnc = encodeURIComponent(userSub);
  const r = await fetch(`${apiBase}/auth/workspaces/${enc}/members/${subEnc}`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${bearerToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ role: (role || '').trim().toUpperCase() }),
  });
  const text = await r.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { message: text };
  }
  if (!r.ok) {
    throw new Error(data.message || data.error || r.statusText || 'Could not update role');
  }
  return data;
}

export async function removeWorkspaceMember(apiBase, bearerToken, tenantId, userSub) {
  const enc = encodeURIComponent(tenantId);
  const subEnc = encodeURIComponent(userSub);
  const r = await fetch(`${apiBase}/auth/workspaces/${enc}/members/${subEnc}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${bearerToken}` },
  });
  const text = await r.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { message: text };
  }
  if (!r.ok) {
    throw new Error(data.message || data.error || r.statusText || 'Could not remove member');
  }
  return data;
}

const PENDING_INVITE_STORAGE_KEY = 'stepflow_pending_invite_token';

/** Set when GET /auth/workspaces listed this token in pendingInvites (email-scoped invites). */
export const PENDING_INVITE_TOKEN_SEEN_KEY = 'stepflow_invite_token_seen_in_pending_list';

/** PendingInviteBanner: sessionStorage key for "Hide for now" — clear when an invite is accepted. */
export const INVITE_BANNER_DISMISS_STORAGE_KEY = 'stepflow_invite_banner_dismissed_for';

export function getPendingInviteToken() {
  try {
    return sessionStorage.getItem(PENDING_INVITE_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

export function setPendingInviteToken(token) {
  try {
    const t = (token || '').trim();
    if (t) sessionStorage.setItem(PENDING_INVITE_STORAGE_KEY, t);
    else sessionStorage.removeItem(PENDING_INVITE_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function clearPendingInviteToken() {
  setPendingInviteToken('');
  try {
    sessionStorage.removeItem(PENDING_INVITE_TOKEN_SEEN_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Create a link invite (optional email — SES sends join URL when email set).
 */
export async function createWorkspaceLinkInvite(apiBase, bearerToken, tenantId, { role, email } = {}) {
  const enc = encodeURIComponent(tenantId);
  const r = await fetch(`${apiBase}/auth/workspaces/${enc}/invites`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${bearerToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      role: role || 'USER',
      ...(email ? { email: String(email).trim().toLowerCase() } : {}),
    }),
  });
  const text = await r.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { message: text };
  }
  if (!r.ok) {
    throw new Error(data.message || data.error || r.statusText || 'Create invite failed');
  }
  return data;
}

export async function listWorkspaceInvites(apiBase, bearerToken, tenantId) {
  const enc = encodeURIComponent(tenantId);
  const r = await fetch(`${apiBase}/auth/workspaces/${enc}/invites`, {
    headers: { Authorization: `Bearer ${bearerToken}` },
  });
  const text = await r.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { message: text };
  }
  if (!r.ok) {
    throw new Error(data.message || data.error || r.statusText || 'Failed to list invites');
  }
  return data;
}

export async function revokeWorkspaceInvite(apiBase, bearerToken, tenantId, token) {
  const enc = encodeURIComponent(tenantId);
  const tok = encodeURIComponent(token);
  const r = await fetch(`${apiBase}/auth/workspaces/${enc}/invites/${tok}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${bearerToken}` },
  });
  const text = await r.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { message: text };
  }
  if (!r.ok) {
    throw new Error(data.message || data.error || r.statusText || 'Revoke failed');
  }
  return data;
}

export async function acceptWorkspaceInvite(apiBase, bearerToken, token) {
  const r = await fetch(`${apiBase}/auth/invites/accept`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${bearerToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token: (token || '').trim() }),
  });
  const text = await r.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { message: text };
  }
  if (!r.ok) {
    const err = new Error(data.message || data.error || r.statusText || 'Could not accept invite');
    err.status = r.status;
    err.code = data.code;
    err.inviteEmail = data.inviteEmail;
    err.signedInEmail = data.signedInEmail;
    throw err;
  }
  return data;
}

export async function rejectWorkspaceInvite(apiBase, bearerToken, token) {
  const r = await fetch(`${apiBase}/auth/invites/reject`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${bearerToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token: (token || '').trim() }),
  });
  const text = await r.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { message: text };
  }
  if (!r.ok) {
    const err = new Error(data.message || data.error || r.statusText || 'Could not reject invite');
    err.status = r.status;
    err.code = data.code;
    err.inviteEmail = data.inviteEmail;
    err.signedInEmail = data.signedInEmail;
    throw err;
  }
  return data;
}
